import clsx from 'clsx';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { ButtonWithIcon } from 'src/components/Buttons';
import { FilterPillList } from 'src/components/common/FilterPill';
import { EventVenueMap } from 'src/components/Events/VenueMap/EventVenueMap';
import { createGetColor } from 'src/components/Listings/InventoryEventPage/utils/colorUtils';
import {
  Content,
  getContent,
  useContent,
  useContentContext,
} from 'src/contexts/ContentContext';
import { useEventMapContext } from 'src/contexts/EventMapContext/EventMapContext';
import { useGetVenueZoneMapConfigInfo } from 'src/contexts/EventMapContext/useGetVenueZoneMapConfigInfo';
import { useGetVenueZoneMapInfoOverrides } from 'src/contexts/EventMapContext/useGetVenueZoneMapConfigInfoOverrides';
import { vars } from 'src/core/themes';
import { Button, Stack, Switch, Tooltip, TooltipPopover } from 'src/core/ui';
import { PillItem } from 'src/core/ui/PillList/PillItem';
import { SectionSelectDialog } from 'src/dialogs/SectionSelectDialog';
import { useBasicDialog } from 'src/hooks/useBasicDialog';
import { useGetEventAutoPricingSettings } from 'src/hooks/useGetEventAutoPricingSettings';
import { EditIcon, IconsFill, InfoOutlineIcon } from 'src/svgs/Viagogo';
import { ContentId } from 'src/utils/constants/contentId';
import { VENUE_ZONE_CONFIG_TYPE_TO_CID } from 'src/utils/constants/contentIdMaps';
import {
  ListingDetailsAutoPricingSectionUpdatesV2,
  SectionSectionGroup,
} from 'src/WebApiController';

import { AutoPricingSettingsInput } from '../../BulkEditAutoPricingSettingsDialog.types';
import { useAutoPricingSettings } from '../../useBulkAutoPricingSettings';
import * as styles from '../AutoPricingSettings.css';
import { RowCompsForManualSections } from '../PricingAgainst/RowCompsForManualSections';
import { PricingSetting } from '../PricingSetting';
import { useBulkEditAutoPricingSettingsSectionSelector } from './BulkEditAutoPricingSettingsSectionSelector.hooks';
import { SectionSelectionMode } from './BulkEditAutoPricingSettingsSectionSelector.types';

type BulkEditAutoPricingSettingsSectionSelectorProps = {
  origin: Pick<AutoPricingSettingsInput, 'compListingSelectedSectionSettings'>;
  canUseZoneMap: boolean | undefined;
};

export const BulkEditAutoPricingSettingsSectionSelector = ({
  origin,
  canUseZoneMap,
}: BulkEditAutoPricingSettingsSectionSelectorProps) => {
  const { watch, setValue } =
    useFormContext<ListingDetailsAutoPricingSectionUpdatesV2>();

  const includeBetterZones = watch('includeBetterZones');

  const {
    sectionSelectionMode,
    setSectionSelectionMode,
    availableSectionSelectionModes,
    selectedSectionsHasChange,
    onResetSelectedSections,
  } = useBulkEditAutoPricingSettingsSectionSelector(origin);

  const headerContentId =
    sectionSelectionMode == SectionSelectionMode.ZoneMap
      ? ContentId.Zones
      : ContentId.Sections;

  return (
    <>
      <PricingSetting
        header={
          <Stack direction="column" gap="s">
            <Content id={headerContentId} />

            {availableSectionSelectionModes.has(SectionSelectionMode.ZoneMap) &&
              (canUseZoneMap ? (
                <div
                  className={styles.sectionSelectionToggle}
                  onClick={() =>
                    setSectionSelectionMode(SectionSelectionMode.ZoneMap)
                  }
                >
                  <Content id={ContentId.UseZoneMap} />{' '}
                </div>
              ) : (
                <Tooltip
                  className={styles.sectionSelectionToggle}
                  triggerContent={
                    <div className={styles.sectionSelectionToggle}>
                      <Content id={ContentId.UseZoneMap} />{' '}
                    </div>
                  }
                  contentVariant="dark"
                >
                  <Content id={ContentId.UseZoneMapDisabledTip} />
                </Tooltip>
              ))}

            {availableSectionSelectionModes.has(
              SectionSelectionMode.CustomSections
            ) && (
              <div
                className={styles.sectionSelectionToggle}
                onClick={() =>
                  setSectionSelectionMode(SectionSelectionMode.CustomSections)
                }
              >
                <Content id={ContentId.ChooseSpecificSections} />
              </div>
            )}

            {availableSectionSelectionModes.has(
              SectionSelectionMode.SameSection
            ) && (
              <div
                className={styles.sectionSelectionToggle}
                onClick={() =>
                  setSectionSelectionMode(SectionSelectionMode.SameSection)
                }
              >
                <Content id={ContentId.UseSameSection} />
              </div>
            )}
          </Stack>
        }
        detail={
          <Stack gap="m" width="full" alignItems="center">
            {sectionSelectionMode == SectionSelectionMode.SameSection && (
              <SameSectionDetail />
            )}

            {sectionSelectionMode == SectionSelectionMode.CustomSections && (
              <CustomSectionDetail origin={origin} />
            )}

            {sectionSelectionMode == SectionSelectionMode.ZoneMap && (
              <ZoneMapDetail />
            )}
          </Stack>
        }
        hasChange={selectedSectionsHasChange}
        hasConflict={origin.compListingSelectedSectionSettings?.hasConflict}
        onReset={onResetSelectedSections}
      />

      {sectionSelectionMode == SectionSelectionMode.ZoneMap && (
        <PricingSetting
          header={
            <Stack direction="row" gap="m" alignItems="center">
              <Content id={ContentId.IncludeBetterZones} />
              <TooltipPopover
                triggerContent={<InfoOutlineIcon size={vars.iconSize.s} />}
                contentVariant="dark"
              >
                <Content id={ContentId.BetterZonesDetail} />
              </TooltipPopover>
            </Stack>
          }
          detail={
            <Switch
              checked={includeBetterZones ?? false}
              onChange={() =>
                setValue('includeBetterZones', !includeBetterZones)
              }
              onCheckedChange={() =>
                setValue('includeBetterZones', !includeBetterZones)
              }
            />
          }
          hasChange={includeBetterZones ?? false}
          hasConflict={false}
          onReset={() => setValue('includeBetterZones', false)}
        />
      )}

      <RowCompsForManualSections />
    </>
  );
};

const SameSectionDetail = () => {
  const mySectionText = useContent(ContentId.MySection);

  return (
    <div>
      <PillItem
        value={''}
        display={mySectionText}
        customPillContainer={styles.pillSelected}
        disabled={true}
      />
    </div>
  );
};

const CustomSectionDetail = ({
  origin,
}: {
  origin: Pick<AutoPricingSettingsInput, 'compListingSelectedSectionSettings'>;
}) => {
  const {
    compListingSelectedSectionSettings,
    includeExistingSelectedSections,
    onIncludeExistingSelectedSectionsChange,
    onCompListingChange,
    onCompListingSectionIdFilterChange,
  } = useAutoPricingSettings();

  const existingSectionsText = useContent(ContentId.ExistingSections);
  const editSections = useContent(ContentId.EditSections);

  const { event, venueMapInfo } = useEventMapContext();

  const sectionSelectDialog = useBasicDialog();

  const selectedSections = useMemo(() => {
    const formSectionFilterIds =
      compListingSelectedSectionSettings?.value?.sectionIdFilter ?? [];
    return (
      venueMapInfo?.sections.filter((section) => {
        return formSectionFilterIds.includes(section.id);
      }) ?? []
    );
  }, [
    compListingSelectedSectionSettings?.value?.sectionIdFilter,
    venueMapInfo?.sections,
  ]);

  const applyRowFilterChanges = useCallback(
    (rowIdFilters: number[], sectionRowIdFilters: { [x: string]: string }) => {
      const compListingSelectedSectionSettingsNew = {
        sectionIdFilter:
          compListingSelectedSectionSettings?.value?.sectionIdFilter ?? [],
        rowIdFilter: Array.from(new Set(rowIdFilters)),
        sectionRowIdFilter: sectionRowIdFilters,
      };

      onCompListingChange({
        compListingSelectedSectionSettingsNew,
        compListingOnlyForSelectedSectionsEnabledNew: true,
      });
    },
    [
      compListingSelectedSectionSettings?.value?.sectionIdFilter,
      onCompListingChange,
    ]
  );

  return event ? (
    <>
      <ButtonWithIcon
        variant="textPlain"
        title={editSections}
        icon={
          <EditIcon
            fill={IconsFill.textBrand}
            size={vars.iconSize.s}
            withHoverEffect
          />
        }
        onClick={sectionSelectDialog.launchDialog}
        style={{ padding: 0, textAlign: 'start' }}
      />

      {origin.compListingSelectedSectionSettings?.hasConflict &&
        includeExistingSelectedSections && (
          <PillItem
            value={includeExistingSelectedSections + ''}
            display={existingSectionsText}
            customPillContainer={
              includeExistingSelectedSections
                ? styles.pillSelected
                : styles.pill
            }
            onDelete={() => onIncludeExistingSelectedSectionsChange(false)}
            disabled={!includeExistingSelectedSections}
          />
        )}

      <div
        style={{
          flex: 1,
          width: '400px',
          overflow: 'auto',
        }}
      >
        <FilterPillList
          selectedSections={selectedSections}
          rowIdFilters={
            compListingSelectedSectionSettings?.value?.rowIdFilter ?? []
          }
          sectionRowIdFilters={
            compListingSelectedSectionSettings?.value?.sectionRowIdFilter ?? {}
          }
          applyRowFilterChanges={applyRowFilterChanges}
          applySelectedSectionChanges={onCompListingSectionIdFilterChange}
          sectionOnly={
            compListingSelectedSectionSettings?.value?.rowOffset != null
          }
          allowRowRange
        />
      </div>

      <SectionSelectDialog
        {...sectionSelectDialog.dialogProps}
        initialSectionIds={
          compListingSelectedSectionSettings?.value?.sectionIdFilter ?? []
        }
        onSave={(selectedSections) => {
          const compListingSelectedSectionSettingsNew = {
            ...compListingSelectedSectionSettings?.value,
            sectionIdFilter: selectedSections.map((s) => s.id),
            rowIdFilter:
              compListingSelectedSectionSettings?.value?.rowIdFilter ?? [],
            sectionRowIdFilter:
              compListingSelectedSectionSettings?.value?.sectionRowIdFilter ??
              {},
          };
          onCompListingChange({ compListingSelectedSectionSettingsNew });
          sectionSelectDialog.closeDialog();
        }}
        onClosed={sectionSelectDialog.closeDialog}
      />
    </>
  ) : (
    <div>
      <PillItem
        value={''}
        display={existingSectionsText}
        customPillContainer={styles.pillSelected}
        disabled={true}
      />
    </div>
  );
};

const ZoneMapDetail = () => {
  const { watch, setValue } = useFormContext<{
    venueZoneMapName: string | null;
  }>();

  const contentContext = useContentContext();

  const { event } = useEventMapContext();

  const [overrideLookup, setOverrideLookup] = useState<
    | {
        [key: string]: SectionSectionGroup;
      }
    | null
    | undefined
  >(null);

  const [defaultLookup, setDefaultLookup] = useState<
    | {
        [key: string]: SectionSectionGroup;
      }
    | null
    | undefined
  >(null);

  const { pricingSettingsQuery } = useGetEventAutoPricingSettings(
    event ?? null
  );

  const venueZoneMapConfigInfoQuery = useGetVenueZoneMapConfigInfo(
    event?.viagId,
    event?.venCfgId
  );

  const venueZoneMapConfigOverrideInfoQuery = useGetVenueZoneMapInfoOverrides(
    event?.viagId,
    event?.venCfgId
  );

  const selectedOverrideId = pricingSettingsQuery.data?.venueZoneOverrideId;
  const defaultConfig =
    pricingSettingsQuery.data?.venueZoneConfigType ??
    venueZoneMapConfigInfoQuery.data?.defaultVenueZoneConfigType;

  const venueNameFromQuery = useMemo(() => {
    if (
      selectedOverrideId != null &&
      venueZoneMapConfigOverrideInfoQuery.data &&
      venueZoneMapConfigOverrideInfoQuery.data[selectedOverrideId]?.name != null
    ) {
      return venueZoneMapConfigOverrideInfoQuery.data[selectedOverrideId].name;
    }

    if (
      defaultConfig != null &&
      venueZoneMapConfigInfoQuery.data?.venueMapZoneConfigs[defaultConfig]
    ) {
      return getContent(
        VENUE_ZONE_CONFIG_TYPE_TO_CID[defaultConfig],
        contentContext
      );
    }
  }, [
    contentContext,
    defaultConfig,
    selectedOverrideId,
    venueZoneMapConfigInfoQuery.data?.venueMapZoneConfigs,
    venueZoneMapConfigOverrideInfoQuery.data,
  ]);

  const currentVenueName = watch('venueZoneMapName');

  useEffect(() => {
    if (
      overrideLookup == null &&
      selectedOverrideId != null &&
      venueZoneMapConfigOverrideInfoQuery.data &&
      venueZoneMapConfigOverrideInfoQuery.data[selectedOverrideId] &&
      !venueZoneMapConfigOverrideInfoQuery.isLoading
    ) {
      setOverrideLookup(
        venueZoneMapConfigOverrideInfoQuery.data[selectedOverrideId]
          .sectionSectionGroupLookup
      );
    }
  }, [
    overrideLookup,
    selectedOverrideId,
    venueZoneMapConfigOverrideInfoQuery.data,
    venueZoneMapConfigOverrideInfoQuery.isLoading,
    setValue,
  ]);

  useEffect(() => {
    if (
      defaultLookup == null &&
      defaultConfig != null &&
      venueZoneMapConfigInfoQuery.data &&
      !venueZoneMapConfigInfoQuery.isLoading
    ) {
      setDefaultLookup(
        venueZoneMapConfigInfoQuery.data.venueMapZoneConfigs[defaultConfig]
      );
    }
  }, [
    defaultLookup,
    defaultConfig,
    venueZoneMapConfigInfoQuery.data,
    venueZoneMapConfigInfoQuery.isLoading,
    contentContext,
    setValue,
  ]);

  useEffect(() => {
    if (venueNameFromQuery && currentVenueName !== venueNameFromQuery) {
      setValue('venueZoneMapName', venueNameFromQuery);
    }
  }, [currentVenueName, venueNameFromQuery, setValue]);

  const sectionSectionGroupLookup = useMemo(() => {
    return overrideLookup ?? defaultLookup;
  }, [overrideLookup, defaultLookup]);

  const [mapOverlay, setMapOverlay] = useState<boolean>(true);

  const toggleZoneMap = useCallback(() => {
    setMapOverlay((prev) => !prev);
  }, []);

  const viewZoneMapText = useContent(ContentId.ViewZoneMap);
  const hideZoneMapText = useContent(ContentId.HideZoneMap);

  const buttonText = useMemo(
    () => (mapOverlay ? viewZoneMapText : hideZoneMapText),
    [hideZoneMapText, mapOverlay, viewZoneMapText]
  );

  return (
    <div className={styles.mapOverlayContainer}>
      <div
        className={clsx(styles.overlayContainer, {
          [styles.overlay]: mapOverlay,
          inactive: !mapOverlay,
        })}
      >
        <span className={styles.mapTitle}>{currentVenueName}</span>
        <Button
          variant="outline"
          className={styles.toggleButton}
          onClick={toggleZoneMap}
        >
          {buttonText}
        </Button>
      </div>
      <div
        className={clsx(styles.zoneMapWrapper, {
          [styles.zoneMapOverlay]: mapOverlay,
        })}
      >
        <div className={styles.zoneMapContainer}>
          <EventVenueMap
            showDefaultMapColors={true}
            getColor={createGetColor(sectionSectionGroupLookup)}
          />
        </div>
      </div>
    </div>
  );
};
